import { type Node } from '@xyflow/react';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';

import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import { type DiagramBuilderStage } from './diagram';
import { STAGE_BAND_HEIGHT, STAGE_GAP } from './diagramLayout.config';
import { type ReactFlowDiagramNode } from './reactFlowDiagram';

/*
 * The stages of a diagram are groups of tiles. Each tile has the ID of its
 * stage. Each stage after the first has a divider: a horizontal line at a
 * saved canvas height, with a label. The stage of a divider holds the tiles
 * from the divider down to the next divider. The first stage holds the tiles
 * above every divider. Only the heights decide which stage is where, so the
 * editor does not depend on the order of the later stages. A stage can be
 * empty, and a label can be blank.
 */

/** The divider before a stage after the first, at its canvas height. */
export interface StageBoundary {
  stageID: string;
  label: string;
  y: number;
}

/** A tile with its canvas box. The stage of a tile is at its vertical center. */
export type StageTile = Pick<Node, 'id' | 'position'> & {
  measured?: { height?: number };
};

/** Returns the vertical center of `tile`, in canvas pixels. */
export function getTileCenterY(tile: StageTile) {
  return tile.position.y + (tile.measured?.height ?? 0) / 2;
}

/**
 * Returns the ID of the stage at canvas height `y`: the stage of the nearest
 * divider at or above `y`, or the first stage above every divider. A point
 * on a divider is in the stage of the divider. `boundaries` can be in any
 * order. Returns undefined without stages.
 */
export function getStageIDAt(
  stages: Pick<DiagramBuilderStage, 'id'>[],
  boundaries: StageBoundary[],
  y: number
): string | undefined {
  const above = boundaries.filter((boundary) => boundary.y <= y);
  return maxBy(above, (boundary) => boundary.y)?.stageID ?? stages[0]?.id;
}

/** Returns the label of the divider before `stage`. */
export function getStageLabel(stage: DiagramBuilderStage) {
  return stage.label ?? '';
}

/**
 * Returns the divider of each stage after the first that has a height.
 *
 * `layoutYs` are the divider heights of the automatic layout, in stage
 * order. They apply when the layout places every tile, because the saved
 * heights belong to the saved tile positions. Otherwise each divider uses
 * its saved height.
 */
export function getStageBoundaries(
  diagram: DiagramBuilder_DiagramFragment,
  layoutYs?: number[] | null
): StageBoundary[] {
  const later = diagram.content.stages.slice(1);
  const useLayout = layoutYs?.length === later.length;
  return later.flatMap((stage, index) => {
    const y = useLayout ? layoutYs[index] : stage.dividerY;
    if (y == null) return [];
    return [{ stageID: stage.id, label: getStageLabel(stage), y }];
  });
}

/** The data of a stage divider node. */
export interface StageDividerData extends Record<string, unknown> {
  label: string;
  /** Opens the label editor. Not set on a locked canvas. */
  onEdit?: () => void;
  /** Removes the divider. Not set on a locked canvas. */
  onDelete?: () => void;
}

export type StageDividerNode = Node<StageDividerData, 'stageDivider'>;
export type StageHighlightNode = Node<Record<string, never>, 'stageHighlight'>;

/** The React Flow node ID of the divider before a stage. */
export const STAGE_DIVIDER_ID_PREFIX = 'stage-divider:';

/** The CSS class of the part of a divider that the user drags. */
export const STAGE_DIVIDER_DRAG_HANDLE = 'stage-divider-handle';

/** Returns the stage ID of a divider node ID, or null for another node. */
export function getDividerStageID(nodeID: string) {
  return nodeID.startsWith(STAGE_DIVIDER_ID_PREFIX)
    ? nodeID.slice(STAGE_DIVIDER_ID_PREFIX.length)
    : null;
}

/** The left edge and width of the tiles, with a gutter for divider labels. */
function getTileSpan(tiles: ReactFlowDiagramNode[]) {
  if (!tiles.length) return { x: -STAGE_GAP, width: STAGE_GAP };
  const left = Math.min(...tiles.map((tile) => tile.position.x));
  const right = Math.max(
    ...tiles.map(
      (tile) => tile.position.x + (tile.measured?.width ?? tile.width ?? 0)
    )
  );
  // The automatic layout keeps this space at the left of the tiles. The fit
  // view includes it, so the divider labels stay clear of the tiles.
  return { x: left - STAGE_GAP, width: right - left + STAGE_GAP };
}

/**
 * Returns a divider node for each boundary. The divider draws its line and
 * label against the visible canvas, so its box only gives it a position.
 * Without `actions`, the dividers are not interactive.
 */
export function getStageDividerNodes(
  boundaries: StageBoundary[],
  tiles: ReactFlowDiagramNode[],
  actions?: {
    onEdit: (stageID: string) => void;
    onDelete: (stageID: string) => void;
  }
): StageDividerNode[] {
  const span = getTileSpan(tiles);
  return boundaries.map((boundary) => ({
    id: `${STAGE_DIVIDER_ID_PREFIX}${boundary.stageID}`,
    type: 'stageDivider',
    position: { x: span.x, y: boundary.y - STAGE_BAND_HEIGHT / 2 },
    width: span.width,
    height: STAGE_BAND_HEIGHT,
    // Each render makes new divider nodes, and React Flow measures a node
    // only when its size changes. The fit view includes only measured nodes,
    // so give the known size. Then the fit view shows every divider.
    measured: { width: span.width, height: STAGE_BAND_HEIGHT },
    zIndex: -1,
    selectable: false,
    deletable: false,
    focusable: false,
    draggable: !!actions,
    dragHandle: `.${STAGE_DIVIDER_DRAG_HANDLE}`,
    data: {
      label: boundary.label,
      onEdit: actions && (() => actions.onEdit(boundary.stageID)),
      onDelete: actions && (() => actions.onDelete(boundary.stageID)),
    },
  }));
}

/**
 * Returns a node that tints the stage with this ID, from its divider to the
 * next divider below it. The first stage starts, and the lowest stage ends,
 * STAGE_GAP past the tiles and dividers.
 */
export function getStageHighlightNode(
  boundaries: StageBoundary[],
  tiles: ReactFlowDiagramNode[],
  stageID: string
): StageHighlightNode {
  const ys = [
    ...boundaries.map((boundary) => boundary.y),
    ...tiles.flatMap((tile) => [
      tile.position.y,
      tile.position.y + (tile.measured?.height ?? 0),
    ]),
  ];
  const top =
    boundaries.find((boundary) => boundary.stageID === stageID)?.y ??
    Math.min(...ys) - STAGE_GAP;
  const below = boundaries.filter((boundary) => boundary.y > top);
  const bottom =
    minBy(below, (boundary) => boundary.y)?.y ?? Math.max(...ys) + STAGE_GAP;
  const span = getTileSpan(tiles);
  return {
    id: 'stage-highlight',
    type: 'stageHighlight',
    position: { x: span.x, y: top },
    width: span.width,
    height: bottom - top,
    zIndex: -2,
    selectable: false,
    draggable: false,
    deletable: false,
    focusable: false,
    data: {},
  };
}
